import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import ResponsiveTable from "./ResponsiveTable";
import PlayerFormDialog from "./PlayerFormDialog";

export default function PlayersDialog({
  open,
  onClose,
  team,
  teams,
  request,
}) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));

  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const [formOpen, setFormOpen] = useState(false);
  const [editingPlayer, setEditingPlayer] = useState(null);

  const loadPlayers = useCallback(async () => {
    if (!team?.id) return;

    setLoading(true);
    setMessage({ type: "", text: "" });

    try {
      const data = await request(
        `/competition/players/?team_id=${team.id}`
      );

      setPlayers(Array.isArray(data) ? data : []);
    } catch (error) {
      setMessage({ type: "error", text: error.message });
    } finally {
      setLoading(false);
    }
  }, [request, team]);

  useEffect(() => {
    if (open) loadPlayers();
  }, [open, loadPlayers]);

  useEffect(() => {
    setPage(0);
  }, [players.length]);

  const handleSavePlayer = async (values) => {
    setSaving(true);
    setMessage({ type: "", text: "" });

    try {
      if (editingPlayer) {
        await request(
          `/competition/players/${editingPlayer.id}/update/`,
          {
            method: "PUT",
            body: JSON.stringify(values),
          }
        );

        setMessage({
          type: "success",
          text: "Jugador actualizado correctamente",
        });
      } else {
        await request(
          "/competition/players/create/",
          {
            method: "POST",
            body: JSON.stringify(values),
          }
        );

        setMessage({
          type: "success",
          text: "Jugador creado correctamente",
        });
      }

      setFormOpen(false);
      setEditingPlayer(null);

      await loadPlayers();
    } catch (error) {
      setMessage({ type: "error", text: error.message });
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePlayer = async (playerId) => {
    setLoading(true);
    setMessage({ type: "", text: "" });

    try {
      await request(
        `/competition/players/${playerId}/delete/`,
        { method: "DELETE" }
      );

      setMessage({
        type: "success",
        text: "Jugador eliminado correctamente",
      });

      await loadPlayers();
    } catch (error) {
      setMessage({ type: "error", text: error.message });
      setLoading(false);
    }
  };

  const columns = useMemo(
    () => [
      { key: "name", label: "Jugador" },
      { key: "jersey_number", label: "Dorsal" },
      { key: "team", label: "Equipo" },
      {
        key: "actions",
        label: "Acciones",
        render: (row) => (
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            sx={{ width: "100%" }}
          >
            <Button
              variant="outlined"
              size="small"
              startIcon={<EditRoundedIcon />}
              onClick={() => {
                setEditingPlayer(row);
                setFormOpen(true);
              }}
            >
              Editar
            </Button>

            <Button
              color="error"
              variant="outlined"
              size="small"
              startIcon={<DeleteRoundedIcon />}
              onClick={() => handleDeletePlayer(row.id)}
            >
              Eliminar
            </Button>
          </Stack>
        ),
      },
    ],
    []
  );

  if (!team) return null;

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullScreen={fullScreen}
        fullWidth
        maxWidth="lg"
        PaperProps={{
          sx: {
            borderRadius: "18px",
          },
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: "primary.main",
            color: "#fff",
            fontWeight: 700,
            fontSize: "1.05rem",
            mb: 3
          }}
        >
          <Stack
            direction="row"
            spacing={1.2}
            alignItems="center"
          >
            <Avatar
              src={team.logo || ""}
              variant="rounded"
              sx={{
                width: 48,
                height: 48,
                borderRadius: "14px",
                bgcolor: "#fff",
              }}
            />

            <Box>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: "1rem",
                  color: "#fff",
                }}
              >
                Jugadores de {team.name}
              </Typography>

              <Typography
                sx={{
                  fontSize: "0.82rem",
                  opacity: 0.9,
                  color: "#fff",
                }}
              >
                {team.category}
              </Typography>
            </Box>
          </Stack>
        </DialogTitle>

        <DialogContent sx={{ pb: 3, pt: 2 }}>
          <Stack
            direction={{
              xs: "column",
              sm: "row",
            }}
            spacing={1.2}
            justifyContent="space-between"
            alignItems={{
              xs: "stretch",
              sm: "center",
            }}
            sx={{ mb: 2 }}
          >
            <Typography
              sx={{
                fontSize: "0.92rem",
                color: "text.secondary",
              }}
            >
              Crea, edita dorsales o mueve jugadores entre equipos.
            </Typography>

            <Button
              variant="contained"
              startIcon={<AddRoundedIcon />}
              onClick={() => {
                setEditingPlayer(null);
                setFormOpen(true);
              }}
            >
              Nuevo jugador
            </Button>
          </Stack>

          {message.text && (
            <Alert
              severity={
                message.type === "success"
                  ? "success"
                  : "error"
              }
              onClose={() =>
                setMessage({ type: "", text: "" })
              }
              sx={{ mb: 2 }}
            >
              {message.text}
            </Alert>
          )}

          <ResponsiveTable
            rows={players}
            columns={columns}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={(_, nextPage) =>
              setPage(nextPage)
            }
            onRowsPerPageChange={(event) => {
              setRowsPerPage(
                parseInt(event.target.value, 10)
              );
              setPage(0);
            }}
            getRowId={(row) => row.id}
            getCardTitle={(row) => row.name}
            emptyText={
              loading
                ? "Cargando jugadores..."
                : "Este equipo todavía no tiene jugadores"
            }
          />
        </DialogContent>
      </Dialog>

      <PlayerFormDialog
        open={formOpen}
        onClose={() => {
          setFormOpen(false);
          setEditingPlayer(null);
        }}
        onSubmit={handleSavePlayer}
        initialData={editingPlayer}
        loading={saving}
        teams={teams}
        defaultTeamId={team.id}
      />
    </>
  );
}